/**
 * Petfinder API integration service
 * Connects to Petfinder API v2 to find real adoptable pets
 * Uses OAuth 2.0 authentication and provides search functionality
 */

// Petfinder API configuration
const PETFINDER_API_BASE = 'https://api.petfinder.com/v2';
const PETFINDER_CLIENT_ID = import.meta.env?.VITE_PETFINDER_API_KEY || '';
const PETFINDER_CLIENT_SECRET = import.meta.env?.VITE_PETFINDER_SECRET || '';

// Token storage
let accessToken = null;
let tokenExpiry = null;

/**
 * Get OAuth access token for Petfinder API
 */
const getAccessToken = async () => {
  // Return cached token if still valid
  if (accessToken && tokenExpiry && Date.now() < tokenExpiry) {
    return accessToken;
  }

  // Check if credentials are available
  if (!PETFINDER_CLIENT_ID || !PETFINDER_CLIENT_SECRET) {
    throw new Error('Petfinder API credentials not configured. Please check your .env file.');
  }

  try {
    const response = await fetch(`${PETFINDER_API_BASE}/oauth2/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        grant_type: 'client_credentials',
        client_id: PETFINDER_CLIENT_ID,
        client_secret: PETFINDER_CLIENT_SECRET,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      // eslint-disable-next-line no-console
      console.error('Petfinder OAuth response:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
        hasCredentials: !!(PETFINDER_CLIENT_ID && PETFINDER_CLIENT_SECRET)
      });
      throw new Error(`OAuth error: ${response.status} - ${response.statusText}`);
    }

    const data = await response.json();
    accessToken = data.access_token;
    tokenExpiry = Date.now() + (data.expires_in * 1000) - 60000; // Expire 1 minute early

    return accessToken;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to get Petfinder access token:', error);
    throw error;
  }
};

/**
 * Make authenticated request to Petfinder API
 */
const petfinderRequest = async (endpoint, params = {}) => {
  try {
    const token = await getAccessToken();
    const url = new URL(`${PETFINDER_API_BASE}${endpoint}`);
    
    // Add query parameters
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        url.searchParams.append(key, params[key]);
      }
    });

    const response = await fetch(url.toString(), {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 400) {
        // Log the full URL for debugging
        // eslint-disable-next-line no-console
        console.error('Petfinder API 400 error - Invalid parameters:', url.toString());
        const errorText = await response.text();
        // eslint-disable-next-line no-console
        console.error('Error details:', errorText);
      }
      throw new Error(`Petfinder API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Petfinder API request failed:', error);
    throw error;
  }
};

/**
 * Convert quiz preferences to Petfinder search parameters
 */
const mapQuizToPetfinderParams = (quizResponses, location) => {
  const params = {
    location: location.zipCode || location.city || '',
    distance: location.radius || 25,
    limit: 20,
    page: 1,
  };

  // Map size preference
  if (quizResponses.size) {
    switch (quizResponses.size) {
      case 'small':
        params.size = ['small', 'medium'].join(',');
        break;
      case 'medium':
        params.size = 'medium';
        break;
      case 'large':
        params.size = ['large', 'xlarge'].join(',');
        break;
      // 'any' - don't set size filter
    }
  }

  // Map experience level to age preference
  if (quizResponses.experience === 'first-time') {
    params.age = ['adult', 'senior'].join(','); // Avoid puppies/kittens for first-time owners
  }

  // Map energy level to activity needs (approximate)
  if (quizResponses.energy) {
    // This would need custom tags or breed filtering in a real implementation
    // For now, we'll use it in our scoring algorithm
  }

  // Add good with children/other pets based on living situation
  if (quizResponses.living === 'house-large' || quizResponses.living === 'farm') {
    params.good_with_children = true;
    params.good_with_dogs = true;
  }

  return params;
};

/**
 * Score a pet based on quiz preferences
 */
const scorePetMatch = (pet, quizResponses) => {
  let score = 50; // Base score

  const attributes = pet.attributes || {};
  
  // Size matching
  if (quizResponses.size && pet.size) {
    const sizePreference = quizResponses.size;
    const petSize = pet.size.toLowerCase();
    
    if (
      (sizePreference === 'small' && ['small', 'medium'].includes(petSize)) ||
      (sizePreference === 'medium' && petSize === 'medium') ||
      (sizePreference === 'large' && ['large', 'xlarge'].includes(petSize)) ||
      sizePreference === 'any'
    ) {
      score += 20;
    }
  }

  // Experience level matching
  if (quizResponses.experience) {
    if (quizResponses.experience === 'first-time') {
      // First-time owners benefit from calmer, trained pets
      if (attributes.house_trained) score += 15;
      if (pet.age === 'Adult' || pet.age === 'Senior') score += 10;
      if (attributes.special_needs === false) score += 10;
    } else if (quizResponses.experience === 'experienced') {
      // Experienced owners can handle more challenging pets
      if (attributes.special_needs) score += 10;
      if (pet.age === 'Young' || pet.age === 'Baby') score += 5;
    }
  }

  // Living situation matching
  if (quizResponses.living) {
    if (quizResponses.living === 'apartment' && attributes.house_trained) {
      score += 15;
    }
    if ((quizResponses.living === 'house-large' || quizResponses.living === 'farm') && 
        attributes.good_with_dogs) {
      score += 10;
    }
  }

  // Time commitment matching
  if (quizResponses.time) {
    if (quizResponses.time === 'minimal' || quizResponses.time === 'moderate') {
      if (pet.age === 'Adult' || pet.age === 'Senior') score += 15;
      if (attributes.special_needs === false) score += 10;
    } else if (quizResponses.time === 'lots' || quizResponses.time === 'all-day') {
      if (pet.age === 'Young' || pet.age === 'Baby') score += 15;
      if (attributes.special_needs) score += 10;
    }
  }

  // Bonus for good attributes
  if (attributes.good_with_children) score += 5;
  if (attributes.good_with_cats) score += 5;
  if (attributes.shots_current) score += 5;
  if (attributes.spayed_neutered) score += 5;

  return Math.min(100, Math.max(0, score));
};

/**
 * Search for pets on Petfinder
 */
export const searchPets = async (quizResponses, location, options = {}) => {
  try {
    const searchParams = mapQuizToPetfinderParams(quizResponses, location);
    
    // Handle breed preference if provided
    if (options.breedPreference) {
      const { breed, animalType } = options.breedPreference;
      // Clean up breed name for Petfinder API
      const cleanBreed = breed.replace(/\s+Dog$/, '').replace(/\s+Cat$/, '').trim();
      if (cleanBreed && cleanBreed.length > 1) {
        searchParams.breed = cleanBreed;
      }
      searchParams.type = animalType;
    }
    
    // Override with any other specific options
    Object.assign(searchParams, options);
    // Remove breedPreference from params as it's not a Petfinder API parameter
    delete searchParams.breedPreference;

    let response;
    try {
      response = await petfinderRequest('/animals', searchParams);
    } catch (apiError) {
      // If breed search fails with 400 error, try without breed filter
      if (apiError.message.includes('400') && searchParams.breed) {
        // eslint-disable-next-line no-console
        console.warn('Breed search failed, retrying without breed filter:', searchParams.breed);
        const fallbackParams = { ...searchParams };
        delete fallbackParams.breed;
        response = await petfinderRequest('/animals', fallbackParams);
      } else {
        throw apiError;
      }
    }
    
    if (!response.animals) {
      return { pets: [], pagination: null };
    }

    // Score and sort pets by compatibility
    const scoredPets = response.animals.map(pet => ({
      ...pet,
      compatibilityScore: scorePetMatch(pet, quizResponses),
    }));

    // Sort by compatibility score (highest first)
    scoredPets.sort((a, b) => b.compatibilityScore - a.compatibilityScore);

    return {
      pets: scoredPets,
      pagination: response.pagination,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Pet search failed:', error);
    throw new Error('Unable to search for pets at this time. Please try again later.');
  }
};

/**
 * Get pet details by ID
 */
export const getPetDetails = async (petId) => {
  try {
    const response = await petfinderRequest(`/animals/${petId}`);
    return response.animal;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to get pet details:', error);
    throw new Error('Unable to load pet details.');
  }
};

/**
 * Search organizations/shelters
 */
export const searchOrganizations = async (location, options = {}) => {
  try {
    const searchParams = {
      location: location.zipCode || location.city || '',
      distance: location.radius || 25,
      limit: 20,
      ...options,
    };

    const response = await petfinderRequest('/organizations', searchParams);
    return response.organizations || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Organization search failed:', error);
    return [];
  }
};

/**
 * Get pet types (dogs, cats, etc.)
 */
export const getPetTypes = async () => {
  try {
    const response = await petfinderRequest('/types');
    return response.types || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to get pet types:', error);
    return [];
  }
};

/**
 * Get breeds for a specific pet type
 */
export const getBreeds = async (petType) => {
  try {
    const response = await petfinderRequest(`/types/${petType}/breeds`);
    return response.breeds || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Failed to get breeds for ${petType}:`, error);
    return [];
  }
};